import axios from 'axios';
import { authService } from './auth';

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || "http://192.20.192.87:7501/api";

// Create axios instance
const api = axios.create({
  baseURL: API_BASE_URL,
  timeout: 15000,
});

// Request interceptor to add auth token
api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('authToken');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Response interceptor: silent token refresh, then forced logout
// Single-flight so concurrent 401s trigger only one refresh call.
let refreshPromise = null;

// Also used by IdleTimeout for activity-based renewal and the Extend button.
export const refreshSession = () => {
  if (!refreshPromise) {
    refreshPromise = authAPI.refresh()
      .then((response) => {
        const token = response.data?.token;
        if (!token) throw new Error('No token in refresh response');
        authService.updateToken(token);
        return token;
      })
      .finally(() => {
        refreshPromise = null;
      });
  }
  return refreshPromise;
};

const forceLogout = () => {
  authService.logout();
  window.location.href = '/login';
};

api.interceptors.response.use(
  (response) => response,
  async (error) => {
    if (error.response?.status === 401) {
      const originalRequest = error.config;
      const url = originalRequest?.url || '';
      const isAuthCall = url.includes('/auth/login') || url.includes('/auth/refresh');
      if (originalRequest && !originalRequest._retry && !isAuthCall && authService.isAuthenticated()) {
        originalRequest._retry = true;
        try {
          const token = await refreshSession();
          originalRequest.headers.Authorization = `Bearer ${token}`;
          return api(originalRequest);
        } catch (refreshError) {
          // Refresh failed (session over) — fall through to logout
        }
      }
      forceLogout();
    }
    return Promise.reject(error);
  }
);

// Auth API
export const authAPI = {
  login: (credentials) => api.post('/auth/login', credentials),
  refresh: () => api.post('/auth/refresh'),
  register: (userData) => api.post('/auth/register', userData),
  changePassword: (passwordData) => api.post('/auth/change-password', passwordData),
    forceChangePassword: (passwordData) => api.post('/auth/force-change-password', passwordData),
};

// Notification API
export const notificationAPI = {
  getAll: (page = 0, size = 20) => api.get(`/notifications?page=${page}&size=${size}`),
  getUnreadCount: () => api.get('/notifications/unread/count'),
  getUnread: () => api.get('/notifications/unread'),
  markAsRead: (notificationId) => api.patch(`/notifications/${notificationId}/read`),
  markAllAsRead: () => api.patch('/notifications/read-all'),  // This is correct
  delete: (notificationId) => api.delete(`/notifications/${notificationId}`),
  deleteAll: () => api.delete('/notifications/all'),
  getById: (notificationId) => api.get(`/notifications/${notificationId}`),
};
// Role API
export const roleAPI = {
  getAll: () => api.get('/roles'),
  getById: (id) => api.get(`/roles/${id}`),
  create: (data) => api.post('/roles', data),
  update: (id, data) => api.put(`/roles/${id}`, data),
  delete: (id) => api.delete(`/roles/${id}`),
};

// User Management API
export const userAPI = {
  // Get all users with pagination
  getAll: (page = 0, size = 20, sortBy = 'id', sortDir = 'asc') => {
    return api.get(`/users?page=${page}&size=${size}&sortBy=${sortBy}&sortDir=${sortDir}`);
  },
  
  // Get user by ID
  getById: (userId) => api.get(`/users/${userId}`),
  
  // Get current user profile
  getProfile: () => api.get('/users/profile'),
  
  // Invite a new user
  invite: (userData) => api.post('/users/invite', userData),
  
  // Update user profile
  update: (userId, userData) => api.put(`/users/${userId}`, userData),
  
  // Update user role
  updateRole: (userId, roleId) => api.patch(`/users/${userId}/role`, { roleId }),
  
  // Deactivate user
  deactivate: (userId) => api.patch(`/users/${userId}/deactivate`),
  
  // Reactivate user
  reactivate: (userId) => api.patch(`/users/${userId}/reactivate`),
  
  // Delete user
  delete: (userId) => api.delete(`/users/${userId}`),
  
  // Resend invitation email
  resendInvitation: (userId) => api.post(`/users/${userId}/resend-invitation`),
};

// Subscription API
export const subscriptionAPI = {
  create: (subscriptionData) => api.post('/subscribe', subscriptionData),
  getAll: () => api.get('/subscriptions'),
  getDashboardStats: () => api.get('/dashboard/stats'),
  // New methods for update and delete
  update: (subscriptionId, data) => api.put(`/subscriptions/${subscriptionId}`, data),
  delete: (subscriptionId) => api.delete(`/subscriptions/${subscriptionId}`),
  // Admin correction with audit trail; send only changed fields plus Reason/NotifyCustomer
  adminEdit: (subscriptionId, data) => api.patch(`/admin/subscriptions/${subscriptionId}`, data),
  cancel: (subscriptionId) => api.put(`/subscriptions/${subscriptionId}/cancel`),
  extend: (subscriptionId, months, billingCycle = null) => {
    const data = {
      action: 'extend',
      months: months,
      ...(billingCycle && { billingCycle })
    };
    return api.put(`/subscriptions/${subscriptionId}`, data);
  },
  changeBilling: (subscriptionId, billingCycle) => {
    const data = {
      action: 'change_billing',
      billingCycle: billingCycle
    };
    return api.put(`/subscriptions/${subscriptionId}`, data);
  }
};

export const paymentAPI = {
  initialize: (subscriptionData) => api.post('/payment/initialize', subscriptionData),
  verify: (reference) => api.get(`/payment/verify/${reference}`),
  check: (reference) => api.get(`/payment/check/${reference}`), 
};

export const pricingAPI = {
  getRates: () => api.get('/pricing/rates'),
  quote: (payload) => api.post('/pricing/quote', payload),
};

// Admin API
export const adminAPI = {
  getDashboard: () => api.get('/admin/dashboard'),
  sendTestEmail: () => api.post('/admin/test-email'),
  changePassword: (passwordData) => api.post('/auth/change-password', passwordData), 
};

// Activity Logs API - Enhanced version
export const activityLogAPI = {
  // Get filtered logs with advanced filters
  getFilteredLogs: (params) => {
    const queryParams = new URLSearchParams();
    
    // Add all params that have values
    Object.keys(params).forEach(key => {
      if (params[key] !== null && params[key] !== undefined && params[key] !== '') {
        queryParams.append(key, params[key]);
      }
    });
    
    return api.get(`/activity-logs?${queryParams.toString()}`);
  },
  
  // Get logs for a specific subscription (legacy support)
  getBySubscription: (subscriptionId, page = 0, size = 20) => {
    return api.get(`/activity-logs?subscriptionId=${subscriptionId}&page=${page}&size=${size}`);
  },
  
  // Get recent logs across all subscriptions (legacy support)
  getRecent: (limit = 20) => {
    return api.get('/activity-logs/recent', {
      params: { limit }
    });
  },
  
  // Get all logs with pagination (legacy support)
  getAll: (page = 0, size = 20) => {
    return api.get(`/activity-logs?page=${page}&size=${size}`);
  },
  
  // Get statistics for a subscription
  getStats: (subscriptionId, days = 30) => {
    return api.get(`/activity-logs/stats/${subscriptionId}`, {
      params: { days }
    });
  },
  
  // Get audit trail for a subscription
  getAuditTrail: (subscriptionId, action) => {
    return api.get(`/activity-logs/audit/${subscriptionId}`, {
      params: { action }
    });
  }
};

// Health check
export const healthCheck = () => api.get('/health');

export default api;